import { useMutation } from "react-query";

import { fetchClient } from "@/lib/fetchClient";
import type { MutationConfig } from "@/lib/react-query";
import { queryClient } from "@/lib/react-query";
import type { Folder } from "@/types/resources";
import { isObject } from "../../../utils/typeGuard";
import type { Workflow } from "../types";

type DeleteFolderDTO = {
  folder_id: string;
  selected_folder_label: string;
};

async function deleteFolder({ folder_id }: DeleteFolderDTO) {
  await fetchClient.DELETE("/folders/{folder_id}", {
    params: { path: { folder_id } },
  });
}

type UseDeleteFolderOptions = {
  config?: MutationConfig<typeof deleteFolder>;
};

export const useDeleteFolder = ({ config }: UseDeleteFolderOptions = {}) => {
  return useMutation({
    onMutate: async (deletedFolder) => {
      await queryClient.cancelQueries("folders");
      const previousFolders = queryClient.getQueryData<{
        folders: Folder[];
        unassigned_workflows: Workflow[];
      }>("folders");

      if (previousFolders) {
        queryClient.setQueryData("folders", {
          ...previousFolders,
          folders: previousFolders.folders.filter(
            (folder) => folder.id !== deletedFolder.folder_id
          ),
        });
      }

      return { previousFolders };
    },
    onError: (_, __, context: unknown) => {
      if (isObject(context) && "previousFolders" in context) {
        queryClient.setQueryData("folders", context["previousFolders"]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries("folders");
    },
    ...config,
    mutationFn: deleteFolder,
  });
};
